import { custom_field_entity_enum } from '@predium/enums';
import isNil from 'lodash/isNil';

export type CustomFieldFilter = {
  entity: custom_field_entity_enum;
  key: string;
  values?: string[] | null;
  min?: number | null;
  max?: number | null;
  equals?: boolean | null;
};

export type CustomFieldFilterValues = Pick<CustomFieldFilter, 'values' | 'min' | 'max' | 'equals'>;

const isCustomFieldFilter = (value: unknown): value is CustomFieldFilter => {
  if (typeof value !== 'object' || isNil(value)) return false;
  if (!('key' in value) || typeof value.key !== 'string') return false;
  if (!('entity' in value) || !Object.values(custom_field_entity_enum).some((entity) => entity === value.entity)) {
    return false;
  }
  return (
    (!('values' in value) ||
      isNil(value.values) ||
      (Array.isArray(value.values) && value.values.every((option) => typeof option === 'string'))) &&
    (!('min' in value) || isNil(value.min) || typeof value.min === 'number') &&
    (!('max' in value) || isNil(value.max) || typeof value.max === 'number') &&
    (!('equals' in value) || isNil(value.equals) || typeof value.equals === 'boolean')
  );
};

export const parseCustomFieldFilters = (value: unknown): CustomFieldFilter[] | null =>
  Array.isArray(value) && value.length > 0 && value.every(isCustomFieldFilter) ? value : null;

export const findCustomFieldFilter = (
  filters: CustomFieldFilter[] | null | undefined,
  entity: custom_field_entity_enum,
  key: string,
): CustomFieldFilter | undefined => filters?.find((filter) => filter.entity === entity && filter.key === key);

const hasAnyValue = ({ values, min, max, equals }: CustomFieldFilterValues): boolean =>
  (!isNil(values) && values.length > 0) || !isNil(min) || !isNil(max) || !isNil(equals);

export const upsertCustomFieldFilter = (
  filters: CustomFieldFilter[] | null | undefined,
  entity: custom_field_entity_enum,
  key: string,
  values: CustomFieldFilterValues,
): CustomFieldFilter[] | null => {
  const others = (filters ?? []).filter((filter) => !(filter.entity === entity && filter.key === key));
  const next = hasAnyValue(values) ? [...others, { entity, key, ...values }] : others;
  return next.length > 0 ? next : null;
};
